/**
 * Scope Breadcrumb — interactive context switch in sidebar header.
 *
 * Fleet scope:   "Fleet · All properties"
 * Property scope: "Fleet → PropertyName ▼"
 *   - "Fleet" is a clickable link → resets to fleet scope, navigates to /app/fleet
 *   - "PropertyName ▼" opens PropertyScopeDropdown
 *
 * Homeowner: shows just property name, no Fleet link, no dropdown.
 */
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { PropertyScopeDropdown } from './PropertyScopeDropdown';

export function ScopeBreadcrumb() {
  const navigate = useNavigate();
  const { navigationScope, setFleetScope, isHomeowner, properties } = useApp();

  // Homeowner with single property: just show property name, no interactivity
  if (isHomeowner) {
    const propName = navigationScope.type === 'property'
      ? navigationScope.propertyName
      : properties[0]?.name;
    return (
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">Scope</p>
        <p className="text-sm font-medium text-sidebar-foreground truncate" title={propName}>
          {propName ?? 'Property'}
        </p>
      </div>
    );
  }

  const handleFleetClick = () => {
    setFleetScope();
    navigate('/app/fleet');
  };

  if (navigationScope.type === 'property') {
    return (
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">Scope</p>
        <div className="flex items-center gap-1 text-sm min-w-0">
          <button
            onClick={handleFleetClick}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0 font-medium"
          >
            Fleet
          </button>
          <span className="text-muted-foreground/50 shrink-0" aria-hidden>&rarr;</span>
          <PropertyScopeDropdown
            currentPropertyId={navigationScope.propertyId}
            currentPropertyName={navigationScope.propertyName}
          />
        </div>
      </div>
    );
  }

  // Fleet scope
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">Scope</p>
      <p className="text-sm font-medium text-sidebar-foreground truncate">
        <span className="text-muted-foreground">Fleet</span>
        <span className="mx-1.5 text-muted-foreground/70" aria-hidden>&middot;</span>
        <span className="truncate">All properties</span>
      </p>
    </div>
  );
}
